import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import WebAppContext from '../../utils/webAppContext';
import BinSetLevelPredictionsFactory from '../../utils/DocumentFactory/binSetLevelPredictionsFactory';
import XLSXBinSetLevelPredictionsStrategy from '../../utils/DocumentFactory/xlsxBinSetLevelPredictionsStrategy';
import { ExportIcon } from '../../atoms/Icons';
import FeedFloButton from '../../atoms/FeedFloButton';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import { useAtomValue } from 'jotai';
import { algorithmVersionAtom } from '../../utils/jotaiAtoms';
import useUser from '../../utils/hooks/useUser';

import './BinSetLevelPredictionsExportButton.scss';

function BinSetLevelPredictionsExportButton({ barnId, barnName }) {
  const { isMetric } = useContext(WebAppContext);
  const gqlClient = useApolloClient();
  const [progress, setProgress] = useState(-1);
  const algorithmVersion = useAtomValue(algorithmVersionAtom);
  const { user } = useUser();

  let buttonText = 'Export Inventory';
  if (progress >= 0) {
    buttonText = `Downloading - ${(progress * 100).toFixed(0)}%`;
  }
  const handleExport = async () => {
    setProgress(0);

    try {
      const factory = new BinSetLevelPredictionsFactory({
        farmId: barnId,
        gqlClient,
        strategy: new XLSXBinSetLevelPredictionsStrategy(isMetric, user?.timezone || 'UTC'),
        progressCallback: (x) => {
          setProgress(x);
        },
        isMetric,
        startTime: null, // Leave unset to download all data
        endTime: null, // Leave unset to download all data
        algorithmVersion,
        timezone: user?.timezone || 'UTC',
      });

      await factory.start();

      const dateFormat = 'YYYY-MM-DD';
      const datePart = `${dayjs().format(dateFormat)}`;
      saveAs(factory.createFile(), `${barnName}_bin_inventory_${datePart}.xlsx`);

      setProgress(-1);
      toast.success('Bin Set Level Predictions Export Ready', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (error) {
      setProgress(-1);
      toast.error('Export failed. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  return (
    <FeedFloButton className="BinSetLevelPredictionsExportButton" disabled={progress >= 0} onClick={handleExport}>
      <ExportIcon />
      <span className="BinSetLevelPredictionsExportButton-text">{buttonText}</span>
    </FeedFloButton>
  );
}

BinSetLevelPredictionsExportButton.propTypes = {
  barnId: PropTypes.string.isRequired,
  barnName: PropTypes.string.isRequired,
};

export default BinSetLevelPredictionsExportButton;
